import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import SpaceSuggestions from '../../../components/admin/spaces/SpaceSuggestions';
import useSpacesHook from '../../../api/spaces';
import FormSpaces from '../../../components/admin/spaces/FormSpaces';
import { Spinner, Message } from '../../../components';
import { checkImageFiles } from '../../../utils/imageTools';

export default function SpaceEditorPage() {
    const { id } = useParams(); // if present => edit
    const edit = Boolean(id);
    const { t } = useTranslation();
    const navigate = useNavigate();

    // form and local state (keep same structure so FormSpaces works unchanged)
    const {
        register, handleSubmit, watch, setValue, reset, control
    } = useForm({
        defaultValues: { confirmed: true, blocked: false },
    });

    const formRef = useRef();
    const [imagesPath, setImagesPath] = useState('');
    const [fileInputs, setFileInputs] = useState([]);

    // ✅ Use your hook WITH the id so it can GET /api/auth/spaces/:id with the token
    const { getSpaceById, postSpace, updateSpace } = useSpacesHook({ id });

    const {
        isLoading: isLoadingPost,
        isSuccess: isSuccessPost,
        mutateAsync: mutateAsyncPost,
    } = postSpace;

    const {
        isLoading: isLoadingUpdate,
        isSuccess: isSuccessUpdate,
        mutateAsync: mutateAsyncUpdate,
    } = updateSpace;

    // ✅ Hydrate the form when the single-space data arrives via the hook
    useEffect(() => {
        if (!edit) return;
        const json = getSpaceById.data;
        if (!json) return;

        const space = json?.space || json; // tolerate backend shape
        const path  = json?.images_path || json?.imagesPath || '';

        setValue('name', space.name);
        setValue('address', space.address);
        setValue('description', space.description);
        setValue('phone', space.phone);
        setValue('postal', space.postal);
        setValue('floors', space.floors);
        setValue('glocation', space.glocation);
        setValue('lat', space.lat);
        setValue('lng', space.lng);
        setValue('admin1', space.admin1);
        setValue('website', space.website);
        setValue('image', space.image);
        setValue('category', space.category);
        setValue('email', space.email);
        setValue('confirmed', space.confirmed);
        setValue('blocked', space.blocked);
        setValue('under_review', space.under_review);
        setValue('images', space.images || []);

        setImagesPath(path || '');
        setFileInputs(
            (space.images || []).map(img => ({
                id: img.id,
                operation: "keep",
                file: null,
                imageData: img.path,
                imagePath: (path || '') + img.path
            }))
        );

        setValue('squareMeters', space.squareMeters || '');
        setValue('includes', space.includes || '');
        setValue('roof', { material: space.roof?.material || '', condition: space.roof?.condition || '' });
        setValue('walls', { material: space.walls?.material || '', condition: space.walls?.condition || '' });
        setValue('coating', { material: space.coating?.material || '', condition: space.coating?.condition || '' });
        setValue('openings', space.openings || '');
        setValue('access', space.access || '');
        setValue('extraNotes', space.extraNotes || '');
        setValue('building_type', space.building_type || '');
    }, [edit, getSpaceById.data, setValue]);

    // After successful create/update (optional navigation)
    useEffect(() => {
        if (isSuccessPost || isSuccessUpdate) {
            // navigate('/admin/spaces');
        }
    }, [isSuccessPost, isSuccessUpdate, navigate]);

    const formCleanHandler = () => {
        reset();
        setFileInputs([]);
        if (!window.close()) {
            // fallback if browser blocks it
            navigate('/admin/spaces');
        }
    };

    const submitHandler = async (data) => {
        try {
            const allImages = checkImageFiles(fileInputs);
            if (edit) {
                await mutateAsyncUpdate({ _id: id, ...data, images: allImages });
            } else {
                await mutateAsyncPost({ ...data, images: allImages });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const busy = (edit && getSpaceById.isLoading) || isLoadingPost || isLoadingUpdate;

    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <title>{edit ? t('Edit Space') : t('Add a NEW Space')}</title>
                </Helmet>
            </HelmetProvider>

            <div className="container mt-3">
                <h3 className="fw-light text-muted">
                    {edit ? t('Edit Space') : t('Add a NEW Space')}
                </h3>

                {edit && getSpaceById.isError && (
                    <Message variant='danger'>
                        {getSpaceById.error?.message || 'Failed to load item'}
                    </Message>
                )}

                {busy ? (
                    <Spinner/>
                ) : (
                    <FormSpaces
                        edit={edit}
                        formCleanHandler={formCleanHandler}
                        isLoading={false}
                        isError={false}
                        errors={{}}
                        isLoadingUpdate={isLoadingUpdate}
                        isLoadingPost={isLoadingPost}
                        register={register}
                        handleSubmit={handleSubmit}
                        submitHandler={submitHandler}
                        watch={watch}
                        error={null}
                        setValue={setValue}
                        t={t}
                        images_path={imagesPath}
                        setFileInputs={setFileInputs}
                        fileInputs={fileInputs}
                        control={control}
                        formRef={formRef}
                    />
                )}
                {edit && (
                    <div className="mt-4">
                        <hr className="my-4" />
                        <SpaceSuggestions spaceId={id} t={t} />
                    </div>
                )}
            </div>
        </>
    );
}
